import React, { useState } from 'react';
import { AUTH_ENDPOINTS, FETCH_OPTIONS } from '../../config/constants';
import { useUser } from '../../context/usercontext';

type AlertType = {
  message: string;
  type: 'error' | 'success';
} | null;

interface LoginProps {
  onLogin: (role: 'admin' | 'user' | 'root') => void;
  onForgotPassword: () => void;
  onFirstLogin: () => void;
}

const LoginPage: React.FC<LoginProps> = ({ onLogin, onForgotPassword, onFirstLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertType>(null);
  const { setUser } = useUser();


  const showAlert = (message: string, type: 'error' | 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      showAlert('Compila tutti i campi', 'error');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(AUTH_ENDPOINTS.LOGIN, {
        method: 'POST',
        ...FETCH_OPTIONS,
        body: JSON.stringify({ email, password })
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg = data?.detail || data?.message || `Errore ${res.status}`;
        showAlert(msg, 'error');
        setLoading(false);
        return;
      }

      const role = data?.role === 'admin' ? 'admin' : (data?.role === 'root' ? 'root' : 'user');
      showAlert(data?.message || data?.detail || 'Login effettuato', 'success');

      setTimeout(() => {
        setLoading(false);
        setUser(data.user || data);
        
        // Controlla se è il primo accesso
        if (data?.first_login === true) {
          onFirstLogin();
        } else {
          onLogin(role);
        }
      }, 1500);

    } catch (error) {
      console.error('Login error:', error);
      showAlert('Impossibile contattare il server. Riprova.', 'error');
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleLogin(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-10 text-center text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
              <span>⏰</span> Timesheet Online
            </h1>
            <p className="opacity-90">Accedi al tuo account</p>
          </div>

          <div className="p-8">
            {alert && (
              <div className={`p-3 rounded-lg mb-5 flex items-center gap-2 ${alert.type === 'error' ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-green-50 border border-green-200 text-green-600'}`}>
                <span>{alert.type === 'error' ? '❌' : '✅'}</span>
                <span>{alert.message}</span>
              </div>
            )}

            <div onKeyPress={handleKeyPress}>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">📧 Email / Username</label>
                <div className="relative">
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@esempio.it"
                    className="w-full p-3 pl-12 border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                    required
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                </div>
              </div>

              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">🔒 Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Inserisci la tua password"
                    className="w-full p-3 pl-12 pr-12 border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                    required
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔑</span>
                  <span 
                    className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-purple-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️‍🗨️' : '👁️'}
                  </span>
                </div>
              </div>

              {/* <div className="flex justify-between items-center mb-6 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span>Ricordami</span>
                </label>
                <button 
                  onClick={onForgotPassword}
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Password dimenticata?
                </button>
              </div> */}

              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full p-4 rounded-lg text-white font-semibold transition-all hover:shadow-lg disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span> Accesso in corso...
                  </span>
                ) : (
                  'Accedi'
                )}
              </button>
            </div>

            {/* <div className="mt-6 pt-6 border-t text-center text-sm text-gray-600">
              Non hai un account? <a href="#" className="text-purple-600 hover:text-purple-800 font-semibold">Registrati ora</a>
            </div> */}
          </div>
        </div>

        <div className="text-center mt-5 text-white text-xs opacity-80">
          © 2024 Timesheet Online - Tutti i diritti riservati
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
